import React, { useImperativeHandle, useState, useEffect } from 'react';
import { useReactive } from 'ahooks';
import { Input, Button, Dropdown, message } from 'kenshin';
import {
  PlusOutlined,
  SearchOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import {
  getAiSessionPage,
  deleteAiSession,
  updateAiSession,
} from '@/_serveice/aiCreation';
import './index.less';

// 获取当前员工ID
function getEmpId() {
  try {
    const emp = JSON.parse(localStorage.getItem('employeeDTO'));
    return emp?.empId;
  } catch {
    return null;
  }
}

const ConversationList = React.forwardRef((props, ref) => {
  const { currentId, onSelect, onNew } = props;
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [menuVisible, setMenuVisible] = useState(null);

  const state = useReactive({
    conversations: [],
    loading: false,
  });

  // 加载会话列表
  const loadConversations = async () => {
    const empId = getEmpId();
    if (!empId) return;

    state.loading = true;
    try {
      const res = await getAiSessionPage({
        empId,
        page: {
          pageNo: 1,
          pageSize: 50,
          sortFields: [{ name: 'lastChatTime', value: 1 }], // 1-倒序
        },
        sessionName: searchValue || undefined,
      });

      if (res?.success && res?.data?.datas) {
        state.conversations = res.data.datas.map((item) => ({
          id: item.sessionId,
          title: item.sessionName || '新对话',
          time: formatTime(item.lastChatTime),
          thumbUrl: getFirstThumbUrl(item.itemList),
          pinned: item.isTop === 1,
          rawData: item,
        }));
      } else {
        state.conversations = [];
      }
    } catch (err) {
      console.error('加载会话列表失败:', err);
      message.error('加载会话列表失败');
    } finally {
      state.loading = false;
    }
  };

  // 首次加载
  useEffect(() => {
    loadConversations();
  }, []);

  // 搜索时重新加载
  useEffect(() => {
    loadConversations();
  }, [searchValue]);

  useImperativeHandle(ref, () => ({
    refresh: loadConversations,
  }));

  // 右键菜单操作
  const handleMenuClick = async ({ key }, conv) => {
    setMenuVisible(null);

    switch (key) {
      case 'rename': {
        const newName = prompt('请输入新的对话名称', conv.title);
        if (newName && newName.trim()) {
          const empId = getEmpId();
          const res = await updateAiSession({
            sessionId: conv.id,
            sessionName: newName.trim(),
            empId,
          });
          if (res?.success) {
            message.success('重命名成功');
            loadConversations();
          } else {
            message.error(res?.errorMessage || '重命名失败');
          }
        }
        break;
      }
      case 'pin': {
        const empId = getEmpId();
        const res = await updateAiSession({
          sessionId: conv.id,
          isTop: conv.pinned ? 0 : 1,
          empId,
        });
        if (res?.success) {
          message.success(conv.pinned ? '已取消置顶' : '已置顶');
          loadConversations();
        } else {
          message.error(res?.errorMessage || '操作失败');
        }
        break;
      }
      case 'delete': {
        const res = await deleteAiSession({ sessionId: conv.id });
        if (res?.success) {
          message.success('已删除');
          if (currentId === conv.id) {
            onNew();
          }
          loadConversations();
        } else {
          message.error(res?.errorMessage || '删除失败');
        }
        break;
      }
      default:
        break;
    }
  };

  const menuItems = (conv) => (
    <Dropdown.Menu>
      <Dropdown.Item>
        <div onClick={() => handleMenuClick({ key: 'rename' }, conv)}>重命名</div>
      </Dropdown.Item>
      <Dropdown.Item>
        <div onClick={() => handleMenuClick({ key: 'pin' }, conv)}>
          {conv.pinned ? '取消置顶' : '置顶'}
        </div>
      </Dropdown.Item>
      <Dropdown.Item>
        <div
          onClick={() => handleMenuClick({ key: 'delete' }, conv)}
          style={{ color: '#ff4d4f' }}
        >
          删除
        </div>
      </Dropdown.Item>
    </Dropdown.Menu>
  );

  const filteredConversations = state.conversations.filter((c) =>
    c.title.toLowerCase().includes(searchValue.toLowerCase()),
  );

  // 置顶的排前面
  const sortedConversations = [
    ...filteredConversations.filter((c) => c.pinned),
    ...filteredConversations.filter((c) => !c.pinned),
  ];

  return (
    <div className="conversationList">
      {/* 顶部操作栏 */}
      <div className="listHeader">
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => {
            onNew();
          }}
          className="newBtn"
        >
          新建对话
        </Button>
        <div className="headerActions">
          {searchVisible ? (
            <Input
              size="small"
              placeholder="搜索对话..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              autoFocus
              onBlur={() => {
                if (!searchValue) setSearchVisible(false);
              }}
              className="searchInput"
              prefix={<SearchOutlined />}
            />
          ) : (
            <SearchOutlined
              className="searchIcon"
              onClick={() => setSearchVisible(true)}
            />
          )}
        </div>
      </div>

      {/* 对话列表 */}
      <div className="listBody">
        {sortedConversations.map((conv) => (
          <div
            key={conv.id}
            className={`convItem ${currentId === conv.id ? 'active' : ''}`}
            onClick={() => onSelect(conv.id)}
          >
            <div className="convThumb">
              {conv.thumbUrl ? (
                <img src={conv.thumbUrl} alt="" />
              ) : (
                <div className="convThumbPlaceholder">AI</div>
              )}
            </div>
            <div className="convInfo">
              <div className="convTitle">{conv.title}</div>
              <div className="convTime">{conv.time}</div>
            </div>
            <Dropdown
              menu={menuItems(conv)}
              trigger="click"
              visible={menuVisible === conv.id}
              onVisibleChange={(v) => setMenuVisible(v ? conv.id : null)}
            >
              <div
                className="moreBtn"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreOutlined />
              </div>
            </Dropdown>
          </div>
        ))}
        {sortedConversations.length === 0 && !state.loading && (
          <div className="emptyTip">暂无对话</div>
        )}
        {state.loading && (
          <div className="emptyTip">加载中...</div>
        )}
      </div>
    </div>
  );
});

// 工具函数：格式化时间
function formatTime(timeStr) {
  if (!timeStr) return '';
  const date = new Date(timeStr);
  const now = new Date();
  const diff = now - date;

  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`;

  return `${date.getMonth() + 1}/${date.getDate()}`;
}

// 工具函数：获取第一张缩略图
function getFirstThumbUrl(itemList) {
  if (!itemList || itemList.length === 0) return null;
  const firstItem = itemList[0];
  if (firstItem.outputList && firstItem.outputList.length > 0) {
    return firstItem.outputList[0].resourceUrl;
  }
  return null;
}

export default ConversationList;
